// src/components/orders/OrdersTable.js

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Alert, AlertDescription } from '../ui/alert';
import OrderActions from './OrderActions';
import OrderSearch from './OrderSearch';
import OrderPagination from './OrderPagination';
import { RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import {
  API_ENDPOINT,
  ENDPOINTS,
  TOKEN_KEY,
  DEFAULT_LIMIT,
  DEFAULT_PAGE,
  QUERY_PARAMS,
  SEARCH_DEBOUNCE_MS,
  ERROR_MESSAGES
} from '../../constants';


const statusMap = {
  0: 'Draft',
  10: 'Payment pending / processing',
  11: 'Unpaid order / Abandoned',
  99: 'Create order failed.',
  100: 'Order created.',
  110: 'Order ready / Label printed',
  200: 'Pending for collection',
  400: 'Pickup start',
  450: 'Arrived for pick-up',
  475: 'Pick-up failed',
  500: 'Pickup success',
  501: 'On-hold',
  555: 'Hub check-in',
  556: 'Hub check-out',
  600: 'Delivery start',
  625: 'Arrived for delivery',
  650: 'Failed to deliver',
  651: 'Failed delivery - Lost/Damage',
  654: 'Failed - Driver cancel',
  655: 'Failed (generic/reason)',
  661: 'Returning to sender',
  663: 'Failed to return',
  700: 'Item Delivered',
  701: 'Item Returned',
  900: 'Order Cancelled',
  1000: 'Order completed'
};


const OrdersTable = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const handleRefresh = () => {
    fetchOrders();
  };

  const { logout } = useAuth();

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem(TOKEN_KEY);
      let url = `${API_ENDPOINT}${ENDPOINTS.ORDERS}?retrieve=${QUERY_PARAMS.RETRIEVE}&limit=${limit}&page=${page}&orderBy=${QUERY_PARAMS.ORDER_BY}&sortBy=${QUERY_PARAMS.SORT_BY}`;

      if (debouncedSearchTerm) {
        url += `&keyword=n=${debouncedSearchTerm}`;
      }

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          logout();
          return;
        }
        throw new Error(ERROR_MESSAGES.FETCH_FAILED);
      }

      const data = await response.json();
      setOrders(data.data || []);
      setTotalRows(data.rowTotal || 0);
      setTotalPages(Math.ceil((data.rowTotal || 0) / limit));
    } catch (error) {
      setError(ERROR_MESSAGES.FETCH_FAILED);
      console.error('Error fetching orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [logout, page, limit, debouncedSearchTerm]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
  };

  if (loading && orders.length === 0) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-white rounded-lg shadow p-4 text-center">
          Loading...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4">
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <div className="bg-white rounded-lg shadow">
        {/* Search and Filters */}
        <div className="p-4 flex items-center justify-between border-b">
          <div className="flex items-center gap-4 w-full">
            <OrderSearch 
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              onSearch={handleSearch}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              className="ml-2 whitespace-nowrap"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Table */}
        {orders.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            No orders found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">
                    Consignment No
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">
                    Created At
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">
                    Service
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {orders.map((order) => (
                  <tr key={order.id}>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {order.consignmentNo}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {new Date(order.createdAt).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {order.service?.name}
                      <br></br><small>{order.serviceCode}</small>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <small><b>{order.statusCode}</b></small><br></br>
                      <small>{statusMap[order.statusCode]}</small>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <OrderActions
                        statusCode={order.statusCode}
                        orderId={order.id}
                        onStatusUpdate={() => {
                          // Refresh the orders list
                          fetchOrders();
                        }}
                        onError={(errorMessage) => {
                          setError(errorMessage);
                          // Error will clear automatically after 5 seconds
                          setTimeout(() => setError(''), 5000);
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <OrderPagination
          page={page}
          setPage={setPage}
          limit={limit}
          setLimit={setLimit}
          totalPages={totalPages}
          totalRows={totalRows}
          currentCount={orders.length}
        />
      </div>
    </div>
  );
};

export default OrdersTable;